/**
 * HLD PDP — Happy Little Doers Product Detail Page
 * Vanilla ES6+ Web Components only. No jQuery.
 */

/* ==========================================================================
   ATF — Quantity Buttons
   ========================================================================== */

document.querySelectorAll('.hld-atf__qty-btn').forEach((btn) => {
  btn.addEventListener('click', () => {
    const input = btn.closest('.hld-atf__quantity').querySelector('.hld-atf__qty-input');
    const current = parseInt(input.value, 10) || 1;
    const action = btn.dataset.action;

    if (action === 'increase') {
      input.value = current + 1;
    } else if (action === 'decrease' && current > 1) {
      input.value = current - 1;
    }

    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
});

/* ==========================================================================
   ATF — Variant Selection
   ========================================================================== */

document.querySelectorAll('.hld-atf__variant-option input[type="radio"]').forEach((radio) => {
  radio.addEventListener('change', () => {
    document.querySelectorAll('.hld-atf__variant-option').forEach((opt) => {
      opt.classList.remove('hld-atf__variant-option--selected');
    });
    radio.closest('.hld-atf__variant-option').classList.add('hld-atf__variant-option--selected');
  });
});

/* ==========================================================================
   ATF — Sticky CTA Bar
   ========================================================================== */

class HldStickyBar extends HTMLElement {
  connectedCallback() {
    this.form = document.querySelector('.hld-atf__form');
    if (!this.form) return;

    this.observer = new IntersectionObserver(
      ([entry]) => {
        const stickyBar = document.getElementById('hld-sticky-cta');
        if (!stickyBar) return;

        if (entry.isIntersecting) {
          stickyBar.classList.remove('is-visible');
          stickyBar.setAttribute('aria-hidden', 'true');
        } else {
          stickyBar.classList.add('is-visible');
          stickyBar.setAttribute('aria-hidden', 'false');
        }
      },
      { threshold: 0 }
    );

    this.observer.observe(this.form);

    const scrollBtn = document.querySelector('[data-scroll-to-form]');
    if (scrollBtn) {
      scrollBtn.addEventListener('click', () => {
        this.form.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

if (!customElements.get('hld-sticky-bar')) {
  customElements.define('hld-sticky-bar', HldStickyBar);
}

// Auto-init sticky bar
(() => {
  const atfSection = document.getElementById('hld-atf');
  if (atfSection) {
    const form = atfSection.querySelector('.hld-atf__form');
    const stickyBar = document.getElementById('hld-sticky-cta');
    if (form && stickyBar) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            stickyBar.classList.remove('is-visible');
            stickyBar.setAttribute('aria-hidden', 'true');
          } else {
            stickyBar.classList.add('is-visible');
            stickyBar.setAttribute('aria-hidden', 'false');
          }
        },
        { threshold: 0 }
      );
      observer.observe(form);

      const scrollBtn = stickyBar.querySelector('[data-scroll-to-form]');
      if (scrollBtn) {
        scrollBtn.addEventListener('click', () => {
          form.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      }
    }
  }
})();

/* ==========================================================================
   FAQ — Accordion
   ========================================================================== */

class HldFaqItem extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('.hld-faq__question');
    this.answer = this.querySelector('.hld-faq__answer');

    if (!this.button || !this.answer) return;

    this.button.addEventListener('click', () => this.toggle());
  }

  toggle() {
    const isOpen = this.button.getAttribute('aria-expanded') === 'true';

    if (isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    this.button.setAttribute('aria-expanded', 'true');
    this.answer.removeAttribute('hidden');
    this.answer.style.maxHeight = this.answer.scrollHeight + 'px';
  }

  close() {
    this.button.setAttribute('aria-expanded', 'false');
    this.answer.style.maxHeight = '0';
    setTimeout(() => {
      this.answer.setAttribute('hidden', '');
    }, 300);
  }
}

if (!customElements.get('hld-faq-item')) {
  customElements.define('hld-faq-item', HldFaqItem);
}

/* ==========================================================================
   Social Proof — Review Filter
   ========================================================================== */

class HldReviewFilter extends HTMLElement {
  connectedCallback() {
    this.buttons = this.querySelectorAll('.hld-proof__filter');
    this.slides = document.querySelectorAll('.hld-proof__slide');

    this.buttons.forEach((btn) => {
      btn.addEventListener('click', () => this.filter(btn));
    });
  }

  filter(activeBtn) {
    const category = activeBtn.dataset.filter;

    this.buttons.forEach((btn) => {
      btn.classList.remove('hld-proof__filter--active');
      btn.setAttribute('aria-selected', 'false');
    });
    activeBtn.classList.add('hld-proof__filter--active');
    activeBtn.setAttribute('aria-selected', 'true');

    this.slides.forEach((slide) => {
      if (category === 'all' || slide.dataset.category === category) {
        slide.removeAttribute('hidden');
      } else {
        slide.setAttribute('hidden', '');
      }
    });
  }
}

if (!customElements.get('hld-review-filter')) {
  customElements.define('hld-review-filter', HldReviewFilter);
}

/* ==========================================================================
   Social Proof — Review Carousel
   ========================================================================== */

class HldReviewCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.hld-proof__track');
    this.prevBtn = this.querySelector('.hld-proof__nav-btn--prev');
    this.nextBtn = this.querySelector('.hld-proof__nav-btn--next');

    if (!this.track) return;

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.scroll('prev'));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.scroll('next'));
    }
  }

  scroll(direction) {
    const slideWidth = this.track.querySelector('.hld-proof__slide')?.offsetWidth || 300;
    const gap = 16;
    const scrollAmount = slideWidth + gap;

    this.track.scrollBy({
      left: direction === 'next' ? scrollAmount : -scrollAmount,
      behavior: 'smooth',
    });
  }
}

if (!customElements.get('hld-review-carousel')) {
  customElements.define('hld-review-carousel', HldReviewCarousel);
}

/* ==========================================================================
   ATF — Image Thumbnails
   ========================================================================== */

document.querySelectorAll('.hld-atf__thumbnail').forEach((thumb) => {
  thumb.addEventListener('click', () => {
    const mainImage = document.querySelector('.hld-atf__hero-image');
    const thumbImage = thumb.querySelector('img');
    if (!mainImage || !thumbImage) return;

    const src = thumbImage.src.replace(/width=\d+/, 'width=1200');
    mainImage.src = src;
    mainImage.srcset = '';

    document.querySelectorAll('.hld-atf__thumbnail').forEach((t) => {
      t.classList.remove('hld-atf__thumbnail--active');
    });
    thumb.classList.add('hld-atf__thumbnail--active');
  });
});
